import { Localization, SettingsManager } from "@viamap/viamap2-common";
import { message } from "src/components/Message";
import { DC } from "src/dataCollector/DataCollector";
import { ExportData } from "src/managers/ExportData";
import { normalizeMatrikelnummerForEjendomSummarisk } from "src/managers/EjendomSummariskMatrikel";
import { buildDownloadFileName } from "src/managers/ServitutDownloadNaming";
import { getCachedPdfBlob } from "src/managers/TingbogPdfBlobCache";
import type { FilingPdfHint } from "src/managers/TingbogFilingPdf";
import { getServitutAktSearchTargets } from "src/modules/ANNModule/servitutAktSearch";

export const TINGBOG_SCANNED_AKT_PREFIX =
  "https://www.tinglysning.dk/tinglysning/rest/indskannetakt/rd/";
export const TINGBOG_NEW_AKT_PREFIX =
  "https://www.tinglysning.dk/tinglysning/rest/pdf/atd/";

/** Thrown for API Gateway 504 so callers can show a specific message (not "tingbog down"). */
export const TINGBOG_GATEWAY_TIMEOUT_ERROR = "TINGBOG_GATEWAY_TIMEOUT";

/** Browser often hides 504 behind CORS (no ACAO on error) → TypeError instead of response.status. */
function isOpaqueGatewayFetchFailure(error: unknown): boolean {
  if (error instanceof TypeError && error.name === "AbortError") {
    return false;
  }
  if (!(error instanceof TypeError)) {
    return false;
  }
  const m = String(error.message).toLowerCase();
  return (
    m.includes("failed to fetch") ||
    m.includes("load failed") ||
    m.includes("networkerror when attempting to fetch")
  );
}


function notifyTingbogDownloadFailure(context: string, error: unknown) {
  console.error(context, error);
  const isGatewayTimeout =
    error instanceof Error && error.message === TINGBOG_GATEWAY_TIMEOUT_ERROR;
  message.error(
    isGatewayTimeout
      ? Localization.getText("Tinglysning PDF gateway timeout")
      : getTingbogDownloadErrorMessage(),
  );
}

type TinglysningGatewayResponse = {
  body?: string;
  url?: string;
};

export type TingbogDocumentRef = Partial<{
  dokumentId: string;
  dokumentFilnavn: string;
  aktAliasId: string;
  aktId: string;
  revisionNummer: string;
  tekster: Array<string | { tekst?: string }>;
  tekstSummarisk: string;
}>;

export type TingbogDownloadTarget =
  | { kind: "akt"; value: string }
  | {
    kind: "gatewayDokumentPdf";
    dokumentIdentifikator: string;
    revisionDokumentIdentifikator?: string;
    filingHint?: FilingPdfHint;
  };

/** Digital tingbog document: PDF via gateway (getDokument), not scanned akt file id. */
export function getTingbogGatewayPdfDokumentId(
  document?: TingbogDocumentRef,
): string | undefined {
  if (!isTingbogElectronicDocument(document)) {
    return undefined;
  }
  const dokumentId = document?.dokumentId?.trim();
  if (!dokumentId) {
    return undefined;
  }
  return dokumentId;
}

export type EjendomSummariskPdfRequest = {
  format: "pdf";
  cadastralDistrictIdentifier: string;
  matrikelnummer: string;
};

export {
  filterCurrentJordstykkerForEjendomSummarisk,
  normalizeMatrikelnummerForEjendomSummarisk,
  toEjendomSummariskMatrikelListe,
} from "src/managers/EjendomSummariskMatrikel";
export type {
  EjendomSummariskJordstykkeRef,
  EjendomSummariskMatrikelRef,
} from "src/managers/EjendomSummariskMatrikel";

const TINGLYSNING_PDF_MIN_INSTANT_MS = Date.UTC(2009, 0, 1, 0, 0, 0, 0);

export function parseTinglysningsDatoToUtcMs(raw: string | undefined): number | null {
  if (!raw?.trim()) {
    return null;
  }
  const s = raw.trim();
  const fromIso = Date.parse(s);
  if (!Number.isNaN(fromIso)) {
    return fromIso;
  }
  const dm = /^(\d{1,2})\.(\d{1,2})\.(\d{4})/.exec(s);
  if (!dm) {
    return null;
  }
  const day = Number(dm[1]);
  const month = Number(dm[2]) - 1;
  const year = Number(dm[3]);
  const t = Date.UTC(year, month, day);
  return Number.isNaN(t) ? null : t;
}

export function isTingbogPdfEligibleByDate(
  tinglysningsDato: string | undefined,
): boolean {
  const ms = parseTinglysningsDatoToUtcMs(tinglysningsDato);
  if (ms === null) {
    return false;
  }
  return ms >= TINGLYSNING_PDF_MIN_INSTANT_MS;
}

export function getTingbogDownloadErrorMessage(): string {
  return Localization.getText("Issues with tingbogen, try again later");
}

/**
 * Accepts `51_M_205`, a tinglysning.dk akt URL, or a `_MAPPE` folder label.
 */
export function parseTingbogAktIdInput(raw: string): string | undefined {
  const trimmed = raw.trim();
  if (!trimmed) {
    return undefined;
  }
  const lastSegment = trimmed.includes("/")
    ? (trimmed.split("/").filter(Boolean).pop() ?? trimmed)
    : trimmed;
  const withoutMappe = lastSegment.replace(/_MAPPE\d+$/i, "").trim();
  return withoutMappe || undefined;
}

/** Accepts a UUID, or a tinglysning.dk `/bilag/{uuid}` URL. */
export function parseTingbogBilagIdInput(raw: string): string | undefined {
  const trimmed = raw.trim();
  if (!trimmed) {
    return undefined;
  }
  const lastSegment = trimmed.includes("/")
    ? (trimmed.split("/").filter(Boolean).pop() ?? trimmed)
    : trimmed;
  const unwrapped = lastSegment.replace(/^\{|\}$/g, "").trim();
  return unwrapped || undefined;
}

export function getTingbogPdfUrl(
  document?: TingbogDocumentRef,
  stamOplysninger?: { indskannetAktDokumentId?: string },
): string | undefined {
  const search = getServitutAktSearchTargets(document, stamOplysninger);
  if (search.korrektAktId) {
    return TINGBOG_SCANNED_AKT_PREFIX + search.korrektAktId;
  }
  if (search.servitutAktId) {
    return TINGBOG_SCANNED_AKT_PREFIX + search.servitutAktId;
  }
  if (!isTingbogElectronicDocument(document) && search.textAktId) {
    return TINGBOG_SCANNED_AKT_PREFIX + search.textAktId;
  }
  const dokumentId = document?.dokumentId?.trim();
  const aktAliasId = document?.aktAliasId?.trim();
  const aktId = document?.aktId?.trim();
  if (aktAliasId && dokumentId) {
    return TINGBOG_NEW_AKT_PREFIX + dokumentId;
  }
  if (!aktId && dokumentId) {
    return TINGBOG_NEW_AKT_PREFIX + dokumentId;
  }
  if (!isTingbogElectronicDocument(document) && search.ovrigAktId) {
    return TINGBOG_SCANNED_AKT_PREFIX + search.ovrigAktId;
  }
  return undefined;
}

export function isTingbogElectronicDocument(
  document?: TingbogDocumentRef
): boolean {
  if (!document) return false;
  if (document.dokumentFilnavn?.trim()) return false;
  if (document.aktAliasId?.trim()) return true;
  const dokumentId = document.dokumentId?.trim();
  const aktId = document.aktId?.trim();
  return Boolean(dokumentId && !aktId);
}

export function shouldRenderTingbogDownloadButton(
  document?: TingbogDocumentRef,
  stamOplysninger?: { indskannetAktDokumentId?: string },
): boolean {
  return getTingbogDownloadTarget(document, stamOplysninger) !== undefined;
}

export function getTingbogDownloadTarget(
  document?: TingbogDocumentRef,
  stamOplysninger?: { indskannetAktDokumentId?: string },
): TingbogDownloadTarget | undefined {
  const search = getServitutAktSearchTargets(document, stamOplysninger);
  if (search.korrektAktId) {
    return { kind: "akt", value: search.korrektAktId };
  }
  if (search.servitutAktId) {
    return { kind: "akt", value: search.servitutAktId };
  }
  if (!isTingbogElectronicDocument(document) && search.textAktId) {
    return { kind: "akt", value: search.textAktId };
  }

  const gatewayId = getTingbogGatewayPdfDokumentId(document);
  if (gatewayId) {
    return { kind: "gatewayDokumentPdf", dokumentIdentifikator: gatewayId };
  }

  if (!isTingbogElectronicDocument(document) && search.ovrigAktId) {
    return { kind: "akt", value: search.ovrigAktId };
  }

  const aktId = document?.aktId?.trim();
  if (aktId) {
    return { kind: "akt", value: aktId };
  }

  return undefined;
}

export function getTingbogDownloadFileName(
  label?: string,
  prefix = "tingbog"
): string {
  return buildDownloadFileName(label, {
    defaultStem: prefix,
    defaultExtension: "pdf",
    preserveSpaces: false,
  });
}

function extractPdfFromSoapXml(xmlText: string): Blob {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlText, "application/xml");
  const ns = "http://rep.oio.dk/tinglysning.dk/schema/elektroniskakt/1/";
  const encodedPdf =
    doc.getElementsByTagNameNS(ns, "IndskannetDokumentData")[0]?.textContent?.trim() ||
    doc.getElementsByTagName("IndskannetDokumentData")[0]?.textContent?.trim() ||
    "";

  if (!encodedPdf) {
    throw new Error("PDF data not found in SOAP response");
  }

  const binary = atob(encodedPdf);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  return new Blob([bytes], { type: "application/pdf" });
}

function base64ToPdfBlob(base64: string): Blob {
  // Gateways may wrap base64 across lines inside JSON; atob rejects whitespace.
  const compact = base64.replace(/\s/g, "");
  const binary = atob(compact);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: "application/pdf" });
}

/**
 * Tinglysning execute-api calls (base URL from `tinglysningApiGatewayHost` in environment settings).
 * getDokument PDF is equivalent to:
 * curl -sS -X POST "{host}/getDokument" -H "x-api-key: …" -H "Content-Type: application/json"
 *   -d '{"dokumentIdentifikator":"<uuid>","format":"pdf"}'
 */
function isMissingTinglysningRouteError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return /\b403\b/.test(message) || /Missing Authentication Token/i.test(message);
}

async function postTinglysningGatewayJson(
  path: string,
  body: Record<string, unknown>,
  signal?: AbortSignal,
  hostOverride?: string,
): Promise<{ body?: string }> {
  const pdfServiceUrl =
    hostOverride?.trim()
    || SettingsManager.getSystemSetting("tinglysningApiGatewayHost")?.trim()
    || "";
  const pdfServiceApiKey =
    SettingsManager.getSystemSetting("tinglysningApiGatewayKey")?.trim() ?? "";

  if (!pdfServiceUrl || !pdfServiceApiKey) {
    throw new Error("Missing tinglysning API configuration");
  }

  const apiUrl = `${pdfServiceUrl.replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`;
  let response: Response;
  try {
    response = await fetch(apiUrl, {
      method: "POST",
      credentials: "omit",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": pdfServiceApiKey,
      },
      body: JSON.stringify(body),
      signal,
    });
  } catch (e) {
    if (isOpaqueGatewayFetchFailure(e)) {
      throw new Error(TINGBOG_GATEWAY_TIMEOUT_ERROR);
    }
    throw e;
  }

  if (!response.ok) {
    const errorText = await response.text().catch(() => "Unknown error");
    if (response.status === 504) {
      throw new Error(TINGBOG_GATEWAY_TIMEOUT_ERROR);
    }
    if (response.status === 503 || /TLWebXML.*503|503.*TLWebXML/i.test(errorText)) {
      throw new Error("Tinglysning service is temporarily unavailable. Please try again later.");
    }
    throw new Error(`Tinglysning API error: ${response.status} - ${errorText.substring(0, 200)}`);
  }

  const parsed = await response.json().catch(() => null);
  const envelope = (parsed ?? {}) as { statusCode?: number; body?: string };

  const bodyStr = typeof envelope.body === "string" ? envelope.body : undefined;
  console.log("[tinglysning gateway]", path, {
    ...envelope,
    body: bodyStr
      ? bodyStr.length > 600
        ? `[base64 length=${bodyStr.length}, preview=${bodyStr.slice(0, 160)}…]`
        : bodyStr
      : envelope.body,
  });

  // Lambda proxy sometimes returns HTTP 200 with an error in the JSON envelope.
  if (
    typeof envelope.statusCode === "number" &&
    envelope.statusCode >= 400
  ) {
    throw new Error(
      (typeof envelope.body === "string" && envelope.body.trim().slice(0, 400)) ||
      `Tinglysning gateway statusCode ${envelope.statusCode}`,
    );
  }
  return envelope;
}

async function pdfBodyFromGateway(
  path: string,
  payload: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<Blob> {
  const raw = await postTinglysningGatewayJson(path, payload, signal);
  const b64 = raw.body?.trim();
  if (!b64) {
    throw new Error("PDF data not found in API response");
  }
  return base64ToPdfBlob(b64);
}

export async function fetchTingbogDokumentPdfBlob(
  dokumentIdentifikator: string,
  revisionDokumentIdentifikator?: string,
  signal?: AbortSignal,
  options?: { skipCache?: boolean; filingHint?: FilingPdfHint },
): Promise<Blob> {
  const id = dokumentIdentifikator.trim();
  if (!id) {
    throw new Error("Missing dokumentIdentifikator");
  }
  const revision = revisionDokumentIdentifikator?.trim() || "";
  const loader = async () => {
    if (!revision) {
      return pdfBodyFromGateway("getDokument", {
        dokumentIdentifikator: id,
        format: "pdf",
      }, signal);
    }

    const history = await getCachedPdfBlob(`gateway-spor:${id}`, () => pdfBodyFromGateway("getDokumentRevisionsspor", {
      dokumentIdentifikator: id,
      format: "pdf",
    }, signal), signal);

    const hint = options?.filingHint;
    if (!hint) {
      return history;
    }
    const { extractPdfPageTexts, pageIndexesForFiling, slicePdfPages } = await import("src/managers/TingbogFilingPdf");
    const pageTexts = await extractPdfPageTexts(history);
    const indexes = pageIndexesForFiling(pageTexts, hint);
    return indexes.length ? slicePdfPages(history, indexes) : history;
  };
  if (options?.skipCache) {
    return loader();
  }
  const hintKey = options?.filingHint
    ? `${options.filingHint.kind}:${options.filingHint.textSnippet || options.filingHint.tidspunkt || ""}`
    : "";
  return getCachedPdfBlob(`gateway:${id}:${revision}:${hintKey}`, loader, signal);
}

export async function fetchTingbogDokumentPdfByPayload(
  payload: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<Blob> {
  const raw = await postTinglysningGatewayJson("getDokument", payload, signal);
  const b64 = raw.body?.trim();
  if (!b64) {
    throw new Error("PDF data not found in API response");
  }
  return base64ToPdfBlob(b64);
}

export async function fetchTingbogDocumentXML(
  dokumentIdentifikator: string
): Promise<Document> {
  const raw = await postTinglysningGatewayJson("getDokument", {
    dokumentIdentifikator: dokumentIdentifikator?.trim() ?? "",
    format: "xml",
  });
  const xmlString = raw.body?.trim();
  if (!xmlString) {
    throw new Error("XML data not found in API response");
  }
  return new DOMParser().parseFromString(xmlString, "application/xml");
}

function revisionssporGatewayHosts(): string[] {
  const primary = SettingsManager.getSystemSetting("tinglysningApiGatewayHost")?.trim() ?? "";
  const etl = SettingsManager.getSystemSetting("eTLServicesUrl")?.trim() ?? "";
  const hosts = [primary, etl];
  if (/\/production\/?$/i.test(primary)) {
    hosts.push(primary.replace(/\/production\/?$/i, "/staging"));
  }
  return [...new Set(hosts.map((host) => host.replace(/\/+$/, "")).filter(Boolean))];
}

export async function fetchTingbogDokumentRevisionssporXml(
  dokumentIdentifikator: string,
  signal?: AbortSignal,
): Promise<string> {
  const id = dokumentIdentifikator.trim();
  if (!id) {
    throw new Error("Missing dokumentIdentifikator");
  }
  const payload = {
    dokumentIdentifikator: id,
    format: "xml",
  };
  const hosts = revisionssporGatewayHosts();
  let lastError: unknown;
  for (const [index, host] of hosts.entries()) {
    try {
      const raw = await postTinglysningGatewayJson("getDokumentRevisionsspor", payload, signal, host);
      const xmlString = raw.body?.trim();
      if (!xmlString) {
        throw new Error("XML data not found in API response");
      }
      return xmlString;
    } catch (error) {
      lastError = error;
      const canRetry = index < hosts.length - 1 && isMissingTinglysningRouteError(error);
      if (!canRetry || signal?.aborted) {
        throw error;
      }
    }
  }
  throw lastError instanceof Error ? lastError : new Error("XML data not found in API response");
}

export async function fetchEjendomSummariskPdfBlob(
  params: EjendomSummariskPdfRequest,
): Promise<Blob> {
  const raw = await postTinglysningGatewayJson("getEjendomSummarisk", {
    cadastralDistrictIdentifier: params.cadastralDistrictIdentifier,
    matrikelnummer: normalizeMatrikelnummerForEjendomSummarisk(params.matrikelnummer),
    format: "pdf",
  });
  const b64 = raw.body?.trim();
  if (!b64) {
    throw new Error("PDF data not found in API response");
  }
  return base64ToPdfBlob(b64);
}

async function resolveTingbogAktFileUrl(aktId: string): Promise<string> {
  const pdfServiceUrl =
    SettingsManager.getSystemSetting("tinglysningApiGatewayHost")?.trim() ?? "";
  const pdfServiceApiKey =
    SettingsManager.getSystemSetting("tinglysningApiGatewayKey")?.trim() ?? "";

  if (!pdfServiceUrl || !pdfServiceApiKey) {
    throw new Error("Missing tinglysning API configuration");
  }

  const apiUrl = `${pdfServiceUrl.replace(/\/+$/, "")}/getTinglysningsAkt`;
  let response: Response;
  try {
    response = await fetch(apiUrl, {
      method: "POST",
      credentials: "omit",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": pdfServiceApiKey,
      },
      body: JSON.stringify({
        AktDokumentId: aktId,
        storeToFile: "true",
      }),
    });
  } catch (e) {
    if (isOpaqueGatewayFetchFailure(e)) {
      throw new Error(TINGBOG_GATEWAY_TIMEOUT_ERROR);
    }
    throw e;
  }

  if (!response.ok) {
    const errorText = await response.text().catch(() => "Unknown error");
    if (response.status === 503 || /TLWebXML.*503|503.*TLWebXML/i.test(errorText)) {
      throw new Error("Tinglysning service is temporarily unavailable. Please try again later.");
    }
    throw new Error(`Tinglysning API error: ${response.status} - ${errorText.substring(0, 200)}`);
  }

  const raw = (await response.json().catch(() => null)) as TinglysningGatewayResponse | null;
  if (!raw) {
    throw new Error("Invalid API response");
  }

  let innerBody: { url?: string } | null = null;
  if (typeof raw.body === "string") {
    try {
      innerBody = JSON.parse(raw.body) as { url?: string };
    } catch {
      innerBody = null;
    }
  }

  const fileUrl = raw.url ?? innerBody?.url;
  if (!fileUrl || typeof fileUrl !== "string") {
    throw new Error("API did not return file URL");
  }

  return fileUrl;
}

async function blobLooksLikeXml(blob: Blob, contentType: string): Promise<boolean> {
  if (contentType.includes("xml")) {
    return true;
  }
  if (contentType.includes("pdf")) {
    return false;
  }
  const head = (await blob.slice(0, 160).text()).trimStart();
  return head.startsWith("<") && !head.startsWith("%PDF");
}

export async function fetchTingbogPdfBlob(aktId: string, signal?: AbortSignal): Promise<Blob> {
  const id = aktId.trim();
  if (!id) {
    throw new Error("Missing akt id");
  }
  return getCachedPdfBlob(`akt:${id}`, async () => {
    const fileUrl = await resolveTingbogAktFileUrl(id);
    const response = await fetch(fileUrl);

    if (!response.ok) {
      throw new Error(`Failed to fetch file from storage (${response.status})`);
    }

    const contentType = (response.headers.get("content-type") || "").toLowerCase();
    const blob = await response.blob();

    if (await blobLooksLikeXml(blob, contentType)) {
      return extractPdfFromSoapXml(await blob.text());
    }

    return blob.type === "application/pdf" ? blob : new Blob([blob], { type: "application/pdf" });
  }, signal);
}

export async function fetchTingbogDocumentBlob(
  target: TingbogDownloadTarget,
  signal?: AbortSignal,
): Promise<Blob> {
  return target.kind === "gatewayDokumentPdf"
    ? fetchTingbogDokumentPdfBlob(
      target.dokumentIdentifikator,
      target.revisionDokumentIdentifikator,
      signal,
      { filingHint: target.filingHint },
    )
    : fetchTingbogPdfBlob(target.value, signal);
}

export async function downloadTingbogBilag(
  bilagRef: string,
  fileName?: string,
): Promise<void> {
  const id = parseTingbogBilagIdInput(bilagRef);
  if (!id) {
    message.error(Localization.getText("Enter a bilag UUID"));
    return;
  }

  try {
    const result = await DC.TL.fetchServitutBilag(id);
    if (!result?.data) {
      message.error(getTingbogDownloadErrorMessage());
      return;
    }
    ExportData.downloadBlob(
      buildDownloadFileName(fileName ?? result.name ?? `bilag_${id}`, {
        defaultStem: `bilag_${id}`,
        defaultExtension: "pdf",
        preserveSpaces: true,
      }),
      result.data,
    );
  } catch (error) {
    notifyTingbogDownloadFailure("Failed to download Tingbog bilag", error);
  }
}

export async function downloadTingbogDocument(
  target: TingbogDownloadTarget | undefined,
  fileName?: string
): Promise<void> {
  if (!target) {
    message.error(getTingbogDownloadErrorMessage());
    return;
  }

  try {
    const blob = await fetchTingbogDocumentBlob(target);
    ExportData.downloadBlob(
      buildDownloadFileName(fileName, {
        defaultStem: "tingbog",
        defaultExtension: "pdf",
        preserveSpaces: true,
      }),
      blob,
    );
  } catch (error) {
    notifyTingbogDownloadFailure("Failed to download Tingbog document", error);
  }
}

export async function downloadEjendomSummariskPdf(
  params: Parameters<typeof DC.TL.EjendomSummariskPDF>[0],
  fileName?: string,
): Promise<void> {
  try {
    const blob = await DC.TL.EjendomSummariskPDF(params);
    ExportData.downloadBlob(
      buildDownloadFileName(fileName ?? "tingbog_attest", {
        defaultStem: "tingbog_attest",
        defaultExtension: "pdf",
        preserveSpaces: true,
      }),
      blob,
    );
  } catch (error) {
    notifyTingbogDownloadFailure("Failed to download ejendom summarisk PDF", error);
  }
}
